import { useIntl } from 'react-intl';

import useSearch from 'src/registry/hooks/useSearch';
import usePagination from 'src/registry/hooks/usePagination';
import { createColumnHelper } from '@tanstack/react-table';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { commonMessages } from 'src/utils/messages';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import { useParams } from 'react-router-dom';
import useFilters from 'src/registry/hooks/useFilters';
import { financialDocumentRowMessages } from 'src/features/financialDocumentRows/utils/messages';
import { useListFinancialDocumentRows } from 'src/features/financialDocumentRows/api/useListFinancialDocumentRows';
import { FinancialDocumentRow } from 'src/features/financialDocumentRows/utils/types';
import CellFinancialDocumentRowActions from 'src/features/financialDocumentRows/components/tables/CellFinancialDocumentRowActions';
import { FINANCIAL_DOCUMENT_STATUS } from 'src/features/financialDocuments/utils/types';
import CreateFinancialDocumentRowFormModal from 'src/features/financialDocumentRows/components/forms/CreateFinancialDocumentRowFormModal';
import { unitsMessages } from 'src/features/units/utils/messages';
import { taxReasonsMessages } from 'src/features/taxReasons/utils/messages';
import useSorting from 'src/registry/hooks/useSorting';
import { CURRENCY } from 'src/types';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

type FinancialDocumentRowsTableProps = {
    documentStatus?: FINANCIAL_DOCUMENT_STATUS;
    currency?: CURRENCY;
};

const FinancialDocumentRowsTable = ({ documentStatus, currency }: FinancialDocumentRowsTableProps) => {
    const { formatMessage, formatNumber } = useIntl();
    const { financialDocumentId = '' } = useParams();
    const permissionManageFinancialDocs = useHasPermissions([PERMISSION.MANAGE_FINANCIAL_DOCUMENTS]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'number', desc: false }],
    });

    const {
        data: financialDocumentRows,
        isLoading,
        isError,
    } = useListFinancialDocumentRows({
        ...(financialDocumentId && { financial_document: financialDocumentId }),
        ...querySearchOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
    });

    const columnHelper = createColumnHelper<FinancialDocumentRow>();

    const columns = [
        columnHelper.accessor('number', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(financialDocumentRowMessages.number),
            },
            maxSize: 45,
        }),
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('unit', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return row.original.unit.name;
            },
            meta: {
                title: formatMessage(unitsMessages.unit),
            },
        }),
        columnHelper.accessor('tax_reason', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const taxReasonPercent = (Number(row.original.tax_reason?.value) * 100).toFixed(2);
                return `${row.original.tax_reason?.name} - ${taxReasonPercent}%`;
            },
            meta: {
                title: formatMessage(taxReasonsMessages.taxReason),
            },
            minSize: 160,
        }),
        columnHelper.accessor('price', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return formatNumber(row.original.price / 100, {
                    style: 'currency',
                    currency: currency,
                });
            },
            meta: {
                title: formatMessage(paymentsMessages.price),
            },
        }),
        columnHelper.accessor('quantity', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(financialDocumentRowMessages.quantity),
            },
        }),
        columnHelper.accessor('amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return formatNumber(row.original.amount / 100, {
                    style: 'currency',
                    currency: currency,
                });
            },
            meta: {
                title: formatMessage(paymentsMessages.amount),
            },
        }),
        columnHelper.accessor('tax_percent', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const displayTaxPercent = (Number(row.original.tax_percent) * 100).toFixed(2);
                return `${displayTaxPercent}%`;
            },
            meta: {
                title: formatMessage(financialDocumentRowMessages.taxPercent),
            },
        }),
        columnHelper.accessor('amount_with_tax', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return formatNumber(row.original.amount_with_tax / 100, {
                    style: 'currency',
                    currency: currency,
                });
            },
            meta: {
                title: formatMessage(paymentsMessages.amountWithTax),
            },
        }),
        ...(permissionManageFinancialDocs
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: ({ row }) => {
                          return <CellFinancialDocumentRowActions row={row} financialDocumentStatus={documentStatus} />;
                      },
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable
            columns={columns}
            data={financialDocumentRows}
            semantics={{
                title: formatMessage(financialDocumentRowMessages.rowsTitle),
                entryName: formatMessage(financialDocumentRowMessages.row).toLowerCase(),
                entryNames: formatMessage(financialDocumentRowMessages.rows).toLowerCase(),
                placeholder: formatMessage(financialDocumentRowMessages.row).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            pagination={pagination}
            filters={filters}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                newEntry:
                    permissionManageFinancialDocs && documentStatus === FINANCIAL_DOCUMENT_STATUS.DRAFT
                        ? CreateFinancialDocumentRowFormModal
                        : undefined,
            }}
        />
    );
};

export default FinancialDocumentRowsTable;
